import {
    Elements,
    PaymentElement,
    useElements,
    useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import React, { useEffect, useState } from "react";
import { BILLING_PLANS, BillingPlanKey, getGstBreakdown } from "../../config/billingPlans";
import {
    confirmPayment,
    createPaymentIntent,
    getStripeConfig,
    mapPlanToPackageId,
} from "../../services/stripePaymentService";

let stripeInstance: Stripe | null = null;

const getStripePromise = async (): Promise<Stripe | null> => {
  if (stripeInstance) return stripeInstance;
  try {
    const config = await getStripeConfig();
    if (!config.publishableKey?.startsWith("pk_"))
      throw new Error("Invalid key");
    stripeInstance = await loadStripe(config.publishableKey);
    return stripeInstance;
  } catch (error) {
    return null;
  }
};

interface PaymentFormProps {
  planKey: BillingPlanKey;
  customerEmail: string;
  customerName: string;
  organizationId?: string;
  onSuccess: (creditsAdded: number) => void;
  onCancel: () => void;
}

const PaymentFormInner: React.FC<PaymentFormProps> = ({
  planKey,
  customerEmail,
  customerName,
  onSuccess,
  onCancel,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState("");
  const [isPaymentReady, setIsPaymentReady] = useState(false);

  const plan = BILLING_PLANS[planKey];
  const isFormValid = stripe && elements && isPaymentReady;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isProcessing || !stripe || !elements) return;

    setIsProcessing(true);
    setError("");

    try {
      // Step 1: Submit the Payment Element to validate all fields
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message || "Please check your payment details");
        setIsProcessing(false);
        return;
      }

      // Step 2: Confirm payment with Stripe using the Payment Element
      const { error: confirmError, paymentIntent } =
        await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: window.location.href,
            payment_method_data: {
              billing_details: { email: customerEmail, name: customerName },
            },
          },
          redirect: "if_required",
        });

      if (confirmError) {
        setError(confirmError.message || "Payment failed");
        setIsProcessing(false);
        return;
      }

      if (paymentIntent?.status === "succeeded") {
        // Step 3: Confirm with backend
        const confirmation = await confirmPayment(paymentIntent.id);
        onSuccess(confirmation.credits || plan.credits);
      } else if (paymentIntent?.status === "processing") {
        // Bank transfers may take time to process
        onSuccess(plan.credits);
      } else {
        setError(`Payment status: ${paymentIntent?.status}. Please try again.`);
        setIsProcessing(false);
      }
    } catch (err: any) {
      setError(err.message || "An error occurred");
      setIsProcessing(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="stripe-payment-form"
      data-testid="stripe-payment-form"
    >
      <div
        className="payment-summary"
        data-testid="payment-summary"
        style={{
          background: "#f0f4ff",
          borderRadius: "8px",
          padding: "16px",
          marginBottom: "20px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          <strong>{plan.name}</strong>
          <span style={{ color: "#6b7280" }}>
            {plan.credits.toLocaleString()} credits
          </span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: "#475569" }}>
          <span>Subtotal</span>
          <span>${(getGstBreakdown(plan.price).subtotalCents / 100).toLocaleString()} AUD</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: "#475569" }}>
          <span>GST (10%)</span>
          <span>${(getGstBreakdown(plan.price).gstCents / 100).toLocaleString()} AUD</span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "18px",
            fontWeight: 600,
            color: "#1a1a2e",
            borderTop: "1px solid #d6e0ff",
            marginTop: 8,
            paddingTop: 8,
          }}
        >
          <span>Total (inc. GST)</span>
          <span>${(getGstBreakdown(plan.price).totalIncGstCents / 100).toLocaleString()} AUD</span>
        </div>
      </div>

      {/* Accepted Payment Methods */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "16px",
          padding: "10px 14px",
          background: "#f9fafb",
          borderRadius: "6px",
          fontSize: "13px",
          color: "#6b7280",
        }}
      >
        <span>Accepted:</span>
        <span
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <span title="Credit / Debit Card">💳 Card</span>
          <span style={{ color: "#d1d5db" }}>|</span>
          <span title="Bank Transfer (BECS Direct Debit)">
            🏦 Bank Transfer
          </span>
          <span style={{ color: "#d1d5db" }}>|</span>
          <span title="Apple Pay"> Apple Pay</span>
          <span style={{ color: "#d1d5db" }}>|</span>
          <span title="Google Pay">G Pay</span>
        </span>
      </div>

      {/* Stripe Payment Element — renders Card, Bank, Apple Pay, Google Pay */}
      <div style={{ marginBottom: "16px" }}>
        <PaymentElement
          options={{
            layout: "tabs",
            business: { name: "ARCaml" },
            fields: {
              billingDetails: { email: "never", name: "never" },
            },
          }}
          onChange={(event) => setIsPaymentReady(event.complete)}
        />
      </div>

      {error && (
        <div
          data-testid="payment-error"
          style={{
            background: "#fef2f2",
            color: "#dc2626",
            padding: "12px",
            borderRadius: "6px",
            marginBottom: "16px",
            fontSize: "14px",
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "12px",
          marginTop: "20px",
        }}
      >
        <button
          type="button"
          className="action-button secondary"
          onClick={onCancel}
          disabled={isProcessing}
          data-testid="payment-back-button"
        >
          Back
        </button>
        <button
          type="submit"
          className="action-button primary"
          disabled={!isFormValid || isProcessing}
          style={{ minWidth: "160px" }}
          data-testid="payment-submit-button"
        >
          {isProcessing
            ? "Processing..."
            : `Pay $${(getGstBreakdown(plan.price).totalIncGstCents / 100).toLocaleString()} AUD`}
        </button>
      </div>
    </form>
  );
};

interface StripePaymentFormProps extends PaymentFormProps {}

export const StripePaymentForm: React.FC<StripePaymentFormProps> = (props) => {
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const plan = BILLING_PLANS[props.planKey];
  const packageId = mapPlanToPackageId(props.planKey);

  useEffect(() => {
    const init = async () => {
      try {
        // Load Stripe
        const promise = getStripePromise();
        setStripePromise(Promise.resolve(promise));

        // Create PaymentIntent upfront so the Payment Element can render all methods
        const piResponse = await createPaymentIntent({
          // Price is derived server-side from packageId (same as the signup
          // flow) so the charged amount — including GST — matches signup.
          packageId,
          customerEmail: props.customerEmail,
          customerName: props.customerName,
          idempotencyKey: `topup_${props.customerEmail}_${packageId}_${Math.floor(Date.now() / 300000)}`,
          metadata: { source: "portal_topup", plan: props.planKey },
        });

        if (piResponse.alreadyPaid) {
          try {
            const confirmation = await confirmPayment(
              piResponse.paymentIntentId,
            );
            props.onSuccess(confirmation.credits || plan.credits);
          } catch {
            props.onSuccess(plan.credits);
          }
          return;
        }

        if (piResponse.clientSecret) {
          setClientSecret(piResponse.clientSecret);
        }

        await promise;
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || !stripePromise || !clientSecret) {
    return (
      <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
        Loading payment form...
      </div>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: "stripe",
          variables: {
            colorPrimary: "#1a1a2e",
            fontFamily:
              '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
            borderRadius: "6px",
          },
        },
      }}
    >
      <PaymentFormInner {...props} />
    </Elements>
  );
};
